import React from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, Repeat, Heart, ArrowRight } from 'lucide-react';
import { PageHero, PillLink } from '@/components/ui-bits';
import { Reveal } from '@/components/motion';
import { LogoMark, OrnamentHeart } from '@/components/Ornaments';
import { ROUTE_SLUGS } from '@/i18n';

const VALUE_ICONS = [ShieldCheck, Repeat, Heart];

export default function About() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const p = (key) => { const s = ROUTE_SLUGS[key][lang]; return s ? `/${lang}/${s}` : `/${lang}`; };
  const valuesRaw = t('about.values', { returnObjects: true });
  const values = Array.isArray(valuesRaw) ? valuesRaw : [];

  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow={t('nav.about')}
        title={t('about.subtitle')}
        ornament={<OrnamentHeart className="hidden md:block absolute right-16 top-20 w-32 text-terracotta/25" />}
      />

      {/* Founding story — why we exist */}
      <section className="px-5 md:px-10 pb-8">
        <div className="max-w-[820px] mx-auto">
          <Reveal><p className="text-xl md:text-2xl text-textDark/85 leading-relaxed mb-6">{t('about.p1')}</p></Reveal>
          <Reveal delay={0.06}><p className="text-xl text-textDark/75 leading-relaxed mb-6">{t('about.p2')}</p></Reveal>
          <Reveal delay={0.12}>
            <p className="font-serif italic text-2xl md:text-3xl text-terracotta leading-snug">{t('about.p3')}</p>
          </Reveal>
        </div>
      </section>

      {/* Central message: we don't replace the family */}
      <section className="px-5 md:px-10 py-14 md:py-20">
        <div className="max-w-[1080px] mx-auto rounded-[2.5rem] bg-salvia/20 border border-salvia/40 px-8 md:px-16 py-14 md:py-20 text-center relative overflow-hidden">
          <OrnamentHeart className="absolute -top-4 right-8 w-24 text-salvia/40 hidden md:block" />
          <Reveal>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight text-textDark">{t('about.familyTitle')}</h2>
            <p className="mt-6 text-xl md:text-2xl text-textDark/80 leading-relaxed max-w-3xl mx-auto">{t('about.familyText')}</p>
          </Reveal>
        </div>
      </section>

      {/* Continuity — dark statement + payoff */}
      <section className="bg-textDark text-beige px-5 md:px-10 py-24 md:py-32 relative overflow-hidden">
        <OrnamentHeart className="absolute -bottom-8 -left-6 w-40 text-terracotta/25" />
        <div className="max-w-[1000px] mx-auto text-center relative z-10">
          <Reveal>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.04]">{t('about.continuityTitle')}</h2>
            <p className="mt-8 text-xl md:text-2xl text-beige/80 leading-relaxed max-w-3xl mx-auto">{t('about.continuityText')}</p>
            <p className="mt-10 font-serif italic text-2xl text-terracotta">{t('footer.tagline')}</p>
          </Reveal>
        </div>
      </section>

      {/* Values */}
      <section className="px-5 md:px-10 py-20 md:py-28">
        <div className="max-w-[1280px] mx-auto">
          <Reveal><h2 className="font-serif text-4xl md:text-5xl text-textDark mb-12 text-center">{t('about.valuesTitle')}</h2></Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {values.map((v, i) => {
              const Icon = VALUE_ICONS[i] || Heart;
              return (
                <Reveal key={i} delay={i * 0.08}>
                  <div data-testid={`about-value-${i}`} className="h-full bg-card rounded-3xl p-8 border border-terracotta/10 text-center">
                    <span className="inline-flex w-16 h-16 items-center justify-center rounded-2xl bg-terracotta/12 text-terracotta mb-6"><Icon size={30} /></span>
                    <h3 className="font-serif text-2xl md:text-3xl text-textDark mb-3">{v.t}</h3>
                    <p className="text-textDark/70 text-lg">{v.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Closing + founders mark + CTA */}
      <section className="bg-rosa px-5 md:px-10 py-20 md:py-28">
        <div className="max-w-[860px] mx-auto text-center">
          <Reveal>
            <span className="inline-block text-terracotta w-20 h-20 mb-6"><LogoMark className="w-full h-full" /></span>
            <p className="text-xl md:text-2xl text-textDark/85 leading-relaxed">{t('about.closing')}</p>
            <p className="mt-6 font-serif text-xl text-textDark">— Laura & Federico</p>
            <div className="mt-9 flex justify-center"><PillLink to={p('contact')} testId="about-cta">{t('about.cta')} <ArrowRight size={20} /></PillLink></div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
